import React, { useState, useEffect } from "react"
import ClassNames from "classnames"

const EMPTY_VALUE = ""
const UP_DOWN = {
  up: 0,
  down: 1,
}

interface props {
  defaultValue: number | ""
  min: number
  max: number
  step: number
  decimalDigits: number
  setValue: Function
  mini?: boolean
  manualOnly?: boolean
  disable?: boolean
}

export const ChartInputNumeric = React.memo((props: props) => {
  // defaultValue → inputValue → numberInputValue → defaultValueの循環で値が変更される。
  const {
    defaultValue,
    min,
    max,
    step,
    decimalDigits,
    setValue,
    mini,
    manualOnly,
    disable,
  } = props
  const [inputValue, setInputValue] = useState(String(defaultValue))
  const [numberInputValue, setNumberInputValue] = useState(defaultValue)
  const [waitFlag, setwaitFlag] = useState(false)
  const [sleep, setSleep] = useState<NodeJS.Timeout>(null)

  //   キー入力 & ボタン入力
  const changeInputValueAtKey = (sign: number) => {
    const _step = !sign ? step : -step
    let _inputValue = inputValue ? Number(inputValue) + _step : 0 + _step
    if (decimalDigits)
      _inputValue =
        Math.round(_inputValue * 10 ** decimalDigits) / 10 ** decimalDigits
    if ((sign && min > _inputValue) || (!sign && _inputValue > max)) return
    // setInputValue(String(_inputValue))
    _setNumberInputValue(String(_inputValue))
  }

  const validationCheck = (numVal: string) => {
    if (numVal === EMPTY_VALUE) return
    if (min > parseFloat(numVal) || parseFloat(numVal) > max) return

    // チェック条件パターン
    let pattern = /^-?\d+\.?\d*$/

    if (min >= 0) {
      if (!decimalDigits) {
        // 数値のみ入力可能
        pattern = /^\d+$/
      } else {
        // 数値及び.のみ入力可能
        pattern = /^\d+\.?\d*$/
      }
    } else if (decimalDigits) {
      // 数値及び-のみ入力可能
      pattern = /^-?\d+$/
    }

    // 数値チェック
    return pattern.test(numVal)
  }

  const changeInputValue = (value: string) => {
    let reg = !decimalDigits
      ? new RegExp(/[!"#$%&'()\*\+\-\.,\/:;<=>?@\[\\\]^_`{|}~]/g)
      : new RegExp(/[!"#$%&'()\*\+\-,\/:;<=>?@\[\\\]^_`{|}~]/g)
    //①,記号系はreturn（小数点有りの場合はカンマは例外）
    //②,①でなくても、数値でない、もしくは空文字であればreturn
    if (reg.test(value) || !(!!parseFloat(value) || value === EMPTY_VALUE)) {
      return
    }
    setInputValue(
      value === EMPTY_VALUE
        ? EMPTY_VALUE
        : value.slice(-1) === "."
        ? value
        : String(parseFloat(value))
    )
  }

  const _setNumberInputValue = (value: string) => {
    if (!value) return setValue("")
    const resuletValue = decimalDigits
      ? Math.round(parseFloat(value) * 10 ** decimalDigits) /
        10 ** decimalDigits
      : parseInt(value)
    if (min > resuletValue) {
      setNumberInputValue(min)
      setInputValue(String(min))
      return
    }
    if (resuletValue > max) {
      setNumberInputValue(max)
      setInputValue(String(max))
      return
    }
    if (validationCheck(value)) setNumberInputValue(resuletValue)
    if (resuletValue === numberInputValue && !waitFlag) setValue(resuletValue)
  }

  useEffect(() => {
    setInputValue(String(defaultValue))
  }, [defaultValue])

  useEffect(() => {
    if (waitFlag) {
      if (sleep) return
      _sleep(100, () => setwaitFlag(false))
    }
    setValue(numberInputValue)
  }, [numberInputValue])

  const _sleep = (waitSec: number, func: Function) => {
    let spanedSec = 0
    let id = setInterval(() => {
      spanedSec += 100

      // 経過時間 >= 待機時間の場合、待機終了。
      if (spanedSec >= waitSec) {
        // タイマー停止
        clearInterval(id)
        setSleep(null)
        // 完了時、コールバック関数を実行
        if (func) func()
      }
    }, 100)
    setSleep(id)
  }

  return (
    <div
      className={ClassNames("spin-control", {
        mini: mini,
        disable,
      })}
    >
      <div className="form-spin">
        <div className="text-field text-field-outlined shaped">
          <input
            type="text"
            className={ClassNames("numeric-input", {
              disable,
            })}
            disabled={disable}
            value={inputValue}
            onWheelCapture={e => {
              if (manualOnly) return
              e.stopPropagation()
              setwaitFlag(true)
              if (e.deltaY > 0) {
                changeInputValueAtKey(UP_DOWN["down"])
              } else {
                changeInputValueAtKey(UP_DOWN["up"])
              }
            }}
            onChange={e => {
              changeInputValue(e.target.value)
            }}
            //   フォーム部品からフォーカスを外した時のイベント
            onBlur={e => {
              _setNumberInputValue(e.target.value)
            }}
            onKeyDown={element => {
              if (manualOnly && element.keyCode !== 13) return
              switch (element.keyCode) {
                // 上キー
                case 38:
                  changeInputValueAtKey(UP_DOWN["up"])
                  break
                // 下キー
                case 40:
                  changeInputValueAtKey(UP_DOWN["down"])
                  break
                // Enter
                case 13:
                  const e = document.activeElement as HTMLElement
                  e.blur()
                  break
                default:
              }
            }}
          />
        </div>
        {!manualOnly && (
          <>
            <b
              className={ClassNames("btn btn-primary btn-numeric icon-ripple", {
                disabled: numberInputValue <= min,
              })}
              // disabled={numberInputValue <= min}
              onClick={() => {
                changeInputValueAtKey(UP_DOWN["down"])
              }}
            >
              <i className="material-icons">remove</i>
            </b>
            <b
              className={ClassNames("btn btn-primary btn-numeric icon-ripple", {
                disabled: numberInputValue >= max,
              })}
              // disabled={numberInputValue >= max}
              onClick={() => {
                changeInputValueAtKey(UP_DOWN["up"])
              }}
            >
              <i className="material-icons">add</i>
            </b>
          </>
        )}
      </div>
    </div>
  )
})

export default ChartInputNumeric
